import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { accountsAPI } from '../services/api';

export default function ActivateAccountPage() {
  const navigate = useNavigate();
  const { uidb64, token } = useParams();

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pwVisible, setPwVisible] = useState(false);
  const [pw2Visible, setPw2Visible] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    accountsAPI
      .validateActivation(uidb64, token)
      .then((res) => {
        if (!mounted) return;
        setValid(true);
        setEmail(res.data?.email || '');
      })
      .catch((err) => {
        if (!mounted) return;
        setValid(false);
        setError(
          err?.response?.data?.detail ||
          'Activation link is invalid or expired.'
        );
      })
      .finally(() => mounted && setLoading(false));
    return () => (mounted = false);
  }, [uidb64, token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== password2) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await accountsAPI.completeActivation({ uidb64, token, password });
      // Success → go to login
      navigate('/login', { replace: true, state: { activated: true, email } });
    } catch (err) {
      setError(
        err?.response?.data?.detail || 'Unable to complete activation.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <h1 className="text-xl font-bold text-gray-900">Activate Admin Account</h1>

        {loading && (
          <p className="mt-4 text-sm text-gray-600">Validating your link…</p>
        )}

        {!loading && !valid && (
          <div className="mt-4">
            <p className="text-sm text-red-600">{error}</p>
            <div className="mt-4">
              <Link to="/login" className="text-primary hover:underline">
                Back to login
              </Link>
            </div>
          </div>
        )}

        {!loading && valid && (
          <>
            <p className="mt-2 text-sm text-gray-600">
              Activation link verified for <span className="font-medium">{email}</span>.
            </p>
            <form className="mt-6 space-y-4" onSubmit={onSubmit}>
              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="flex">
                  <input
                    type={pwVisible ? 'text' : 'password'}
                    className="flex-1 px-3 py-2 border rounded-l-lg outline-none focus:ring-2 focus:ring-primary"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setPwVisible(v => !v)}
                    className="px-3 border-t border-b border-r rounded-r-lg text-sm text-gray-600 hover:bg-gray-50"
                  >
                    {pwVisible ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="flex">
                  <input
                    type={pw2Visible ? 'text' : 'password'}
                    className="flex-1 px-3 py-2 border rounded-l-lg outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Re-enter password"
                    value={password2}
                    onChange={(e) => setPassword2(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setPw2Visible(v => !v)}
                    className="px-3 border-t border-b border-r rounded-r-lg text-sm text-gray-600 hover:bg-gray-50"
                  >
                    {pw2Visible ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className={`w-full px-4 py-2 rounded-lg text-white ${submitting ? 'bg-gray-400' : 'bg-primary hover:bg-primary-dark'} transition-colors`}
              >
                {submitting ? 'Setting Password…' : 'Set Password & Activate'}
              </button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-gray-600 hover:underline">
                  Back to login
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
